import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BusinessIcon from '@mui/icons-material/Business'
import StoreIcon from '@mui/icons-material/Store'
import { supabase } from '../../config/supabase'

interface UpdateResult {
  name: string
  rating?: number
  reviewCount?: number
  error?: string
}

interface BatchResults {
  undertakers: {
    updated: number
    failed: number
    total: number
    details: UpdateResult[]
  }
  chains: {
    updated: number
    failed: number
    total: number
    details: UpdateResult[]
  }
}

export default function BulkUpdate() {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<BatchResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<string>('')
  const [progressPercent, setProgressPercent] = useState(0)

  const runTrustpilotUpdate = async () => {
    setLoading(true)
    setError(null)
    setResults(null)
    setProgress('Starter opdatering...')
    setProgressPercent(0)

    const allResults: BatchResults = {
      undertakers: { updated: 0, failed: 0, total: 0, details: [] },
      chains: { updated: 0, failed: 0, total: 0, details: [] }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        throw new Error('Du skal være logget ind')
      }

      // Process undertakers first
      setProgress('Opdaterer bedemænd...')
      let hasMore = true
      let offset = 0
      
      while (hasMore) {
        const { data, error: fnError } = await supabase.functions.invoke('batch-update-trustpilot', {
          body: { type: 'undertakers', offset },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        })

        if (fnError) throw fnError
        if (data.error) throw new Error(data.error)

        // Accumulate results
        allResults.undertakers.updated += data.updated
        allResults.undertakers.failed += data.failed
        allResults.undertakers.total = data.total
        allResults.undertakers.details.push(...data.details)

        hasMore = data.hasMore
        offset = data.nextOffset

        // Update progress
        const processed = allResults.undertakers.updated + allResults.undertakers.failed
        const percent = data.total > 0 ? Math.round((processed / data.total) * 50) : 0
        setProgressPercent(percent)
        setProgress(`Opdaterer bedemænd... (${processed}/${data.total})`)
        
        // Update intermediate results
        setResults({ ...allResults })
      }

      // Process chains
      setProgress('Opdaterer kæder...')
      hasMore = true
      offset = 0
      
      while (hasMore) {
        const { data, error: fnError } = await supabase.functions.invoke('batch-update-trustpilot', {
          body: { type: 'chains', offset },
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        })

        if (fnError) throw fnError
        if (data.error) throw new Error(data.error)

        // Accumulate results
        allResults.chains.updated += data.updated
        allResults.chains.failed += data.failed
        allResults.chains.total = data.total
        allResults.chains.details.push(...data.details)

        hasMore = data.hasMore
        offset = data.nextOffset

        // Update progress
        const processed = allResults.chains.updated + allResults.chains.failed
        const percent = 50 + (data.total > 0 ? Math.round((processed / data.total) * 50) : 0)
        setProgressPercent(percent)
        setProgress(`Opdaterer kæder... (${processed}/${data.total})`)
        
        // Update intermediate results
        setResults({ ...allResults })
      }

      setProgressPercent(100)
      setProgress('Færdig!')
      setResults(allResults)

    } catch (err: any) {
      console.error('Bulk update error:', err)
      setError(err.message || 'Der opstod en fejl under opdateringen')
      // Still show partial results if any
      if (allResults.undertakers.details.length > 0 || allResults.chains.details.length > 0) {
        setResults(allResults)
      }
    } finally {
      setLoading(false)
      setTimeout(() => {
        setProgress('')
        setProgressPercent(0)
      }, 2000)
    }
  }

  const totalUpdated = results ? results.undertakers.updated + results.chains.updated : 0
  const totalFailed = results ? results.undertakers.failed + results.chains.failed : 0
  const totalProfiles = results ? results.undertakers.total + results.chains.total : 0

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Bulk Opdatering
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color="primary" />
          Trustpilot Ratings
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Opdater Trustpilot ratings og anmeldelsesantal for alle bedemænd og kæder med en Trustpilot URL.
          Dette henter de nyeste data fra Trustpilot og gemmer dem i databasen.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Bemærk:</strong> Opdateringen kører i batches for at undgå timeout. 
          Du kan følge fremgangen nedenfor.
        </Alert>

        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={runTrustpilotUpdate}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? 'Opdaterer...' : 'Start Opdatering'}
        </Button>

        {loading && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {progress}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progressPercent}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {results && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Resultat
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Chip 
                icon={<CheckCircleIcon />} 
                label={`${totalUpdated} af ${totalProfiles} opdateret`} 
                color="success" 
                variant="outlined"
              />
              {totalFailed > 0 && (
                <Chip 
                  icon={<ErrorIcon />} 
                  label={`${totalFailed} fejlet`} 
                  color="error" 
                  variant="outlined"
                />
              )}
            </Box>

            {/* Undertakers Results */}
            <Accordion defaultExpanded={results.undertakers.details.length > 0 && results.undertakers.details.length <= 20}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography fontWeight={500}>
                    Bedemænd ({results.undertakers.updated} opdateret, {results.undertakers.failed} fejlet af {results.undertakers.total})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {results.undertakers.details.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {item.error ? (
                          <ErrorIcon color="error" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          item.error 
                            ? item.error 
                            : `Rating: ${item.rating} | Anmeldelser: ${item.reviewCount ?? 'N/A'}`
                        }
                        secondaryTypographyProps={{
                          color: item.error ? 'error' : 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                  {results.undertakers.details.length === 0 && (
                    <ListItem>
                      <ListItemText 
                        secondary="Ingen bedemænd med Trustpilot URL fundet" 
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Chains Results */}
            <Accordion defaultExpanded={results.chains.details.length > 0 && results.chains.details.length <= 20}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StoreIcon color="primary" />
                  <Typography fontWeight={500}>
                    Kæder ({results.chains.updated} opdateret, {results.chains.failed} fejlet af {results.chains.total})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {results.chains.details.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {item.error ? (
                          <ErrorIcon color="error" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        secondary={
                          item.error 
                            ? item.error 
                            : `Rating: ${item.rating} | Anmeldelser: ${item.reviewCount ?? 'N/A'}`
                        }
                        secondaryTypographyProps={{
                          color: item.error ? 'error' : 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                  {results.chains.details.length === 0 && (
                    <ListItem>
                      <ListItemText 
                        secondary="Ingen kæder med Trustpilot URL fundet" 
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
